import { workspaceBlobUrl } from '@affine/core/modules/workspace/utils/blob-cache';
import { useWorkspaceBlobObjectUrl } from '@affine/core/modules/workspace/views/use-workspace-blob-object-url';
import {
  createElement,
  type CSSProperties,
  type ReactNode,
  useMemo,
} from 'react';

export type ResponsiveImageRole =
  | 'hero'
  | 'card'
  | 'article'
  | 'related'
  | 'cover';

interface ResponsiveImageVariant {
  width: number;
  url: string;
}

interface PublishedResponsiveImageAsset {
  kind: 'responsive-image';
  src: string;
  srcSet: {
    avif: ResponsiveImageVariant[];
    webp: ResponsiveImageVariant[];
    fallback: ResponsiveImageVariant[];
  };
}

interface RoleConfig {
  width: number;
  height: number;
  sizes: string;
}

const ROLE_CONFIG: Record<ResponsiveImageRole, RoleConfig> = {
  hero: { width: 1600, height: 900, sizes: '100vw' },
  card: {
    width: 800,
    height: 450,
    sizes: '(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw',
  },
  article: {
    width: 320,
    height: 180,
    sizes: '(max-width: 639px) 100vw, 240px',
  },
  related: {
    width: 480,
    height: 270,
    sizes: '(min-width: 900px) 25vw, (min-width: 640px) 50vw, 100vw',
  },
  cover: { width: 1200, height: 675, sizes: 'min(960px, 90vw)' },
};

export interface ResponsiveImageProps {
  image: unknown;
  role: ResponsiveImageRole;
  alt?: string;
  priority?: boolean;
  workspaceId?: string;
  allowWorkspaceBlobFallback?: boolean;
  className?: string;
  style?: CSSProperties;
  dataSlRole?: string;
}

export function ResponsiveImage({
  image,
  role,
  alt = '',
  priority = false,
  workspaceId,
  allowWorkspaceBlobFallback = false,
  className,
  style,
  dataSlRole,
}: ResponsiveImageProps): ReactNode {
  const resolved = resolveResponsiveImage(image, {
    workspaceId,
    allowWorkspaceBlobFallback,
  });
  const workspaceBlob = useMemo(
    () =>
      resolved && allowWorkspaceBlobFallback
        ? parseWorkspaceBlobUrl(resolved.src)
        : null,
    [allowWorkspaceBlobFallback, resolved]
  );
  const workspaceBlobObjectUrl = useWorkspaceBlobObjectUrl(
    workspaceBlob?.workspaceId,
    workspaceBlob?.blobId
  );
  if (!resolved) return null;

  const config = ROLE_CONFIG[role];
  const imgProps: Record<string, unknown> = {
    src: workspaceBlobObjectUrl ?? resolved.src,
    alt,
    width: config.width,
    height: config.height,
    loading: priority ? 'eager' : 'lazy',
    decoding: 'async',
    fetchPriority: priority ? 'high' : 'auto',
    className,
    style,
    'data-sl-responsive-image': role,
    ...(dataSlRole ? { 'data-sl-role': dataSlRole } : {}),
  };

  return createElement(
    'picture',
    null,
    resolved.asset
      ? createElement('source', {
          type: 'image/avif',
          sizes: config.sizes,
          srcSet: toSrcSet(resolved.asset.srcSet.avif),
        })
      : null,
    resolved.asset
      ? createElement('source', {
          type: 'image/webp',
          sizes: config.sizes,
          srcSet: toSrcSet(resolved.asset.srcSet.webp),
        })
      : null,
    createElement('img', {
      ...imgProps,
      ...(resolved.asset
        ? {
            srcSet: toSrcSet(resolved.asset.srcSet.fallback),
            sizes: config.sizes,
          }
        : {}),
    })
  );
}

export function resolveResponsiveImage(
  value: unknown,
  options: { workspaceId?: string; allowWorkspaceBlobFallback?: boolean } = {}
): { src: string; asset: PublishedResponsiveImageAsset | null } | null {
  if (typeof value === 'string' && value.trim() !== '') {
    const src = normalizeAllowedImageUrl(
      value,
      options.allowWorkspaceBlobFallback
    );
    return src ? { src, asset: null } : null;
  }
  if (isPublishedResponsiveImageAsset(value)) {
    return { src: value.src, asset: value };
  }
  const attachmentUrl = resolveAttachmentUrl(value, options);
  if (attachmentUrl) return { src: attachmentUrl, asset: null };
  return null;
}

function resolveAttachmentUrl(
  value: unknown,
  options: { workspaceId?: string; allowWorkspaceBlobFallback?: boolean }
): string | null {
  if (Array.isArray(value)) {
    for (const item of value) {
      const url = resolveAttachmentUrl(item, options);
      if (url) return url;
    }
    return null;
  }

  if (!value || typeof value !== 'object') return null;
  const record = value as Record<string, unknown>;

  if (record['_type'] === 'attachment' && Array.isArray(record['files'])) {
    return resolveAttachmentUrl(record['files'], options);
  }

  if (Array.isArray(record['files'])) {
    return resolveAttachmentUrl(record['files'], options);
  }

  const direct = readString(record, ['downloadUrl', 'url', 'src', 'href']);
  if (direct) {
    return normalizeAllowedImageUrl(direct, options.allowWorkspaceBlobFallback);
  }

  const blobId = readAttachmentBlobId(record);
  if (blobId && options.workspaceId && options.allowWorkspaceBlobFallback) {
    return workspaceBlobUrl(options.workspaceId, blobId);
  }

  const nestedFiles = Object.entries(record)
    .map(([key, item]) =>
      item && typeof item === 'object'
        ? {
            key,
            file: item as Record<string, unknown>,
          }
        : null
    )
    .filter(
      (
        item
      ): item is {
        key: string;
        file: Record<string, unknown>;
      } => item !== null && isAttachmentLikeRecord(item.file, item.key)
    )
    .sort((a, b) => readOrder(a.file) - readOrder(b.file));

  for (const item of nestedFiles) {
    const nestedDirect = readString(item.file, [
      'downloadUrl',
      'url',
      'src',
      'href',
    ]);
    if (nestedDirect) {
      const allowed = normalizeAllowedImageUrl(
        nestedDirect,
        options.allowWorkspaceBlobFallback
      );
      if (allowed) return allowed;
    }
    const nestedBlobId = readAttachmentBlobId(item.file, item.key);
    if (
      nestedBlobId &&
      options.workspaceId &&
      options.allowWorkspaceBlobFallback
    ) {
      return workspaceBlobUrl(options.workspaceId, nestedBlobId);
    }
  }

  return null;
}

function readString(
  record: Readonly<Record<string, unknown>>,
  keys: readonly string[]
): string | null {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string' && value.trim() !== '') return value.trim();
  }
  return null;
}

function readAttachmentBlobId(
  record: Readonly<Record<string, unknown>>,
  fallbackKey?: string
): string | null {
  const direct = readString(record, ['blobId', 'sourceId', 'id']);
  if (direct) return direct;
  return fallbackKey && fallbackKey.trim() !== '' ? fallbackKey.trim() : null;
}

function isAttachmentLikeRecord(
  record: Readonly<Record<string, unknown>>,
  fallbackKey?: string
): boolean {
  if (readString(record, ['downloadUrl', 'url', 'src', 'href'])) return true;
  if (readString(record, ['blobId', 'sourceId'])) return true;
  if (!readAttachmentBlobId(record, fallbackKey)) return false;
  return (
    typeof record['name'] === 'string' ||
    typeof record['mime'] === 'string' ||
    typeof record['order'] === 'number' ||
    typeof record['size'] === 'number'
  );
}

function readOrder(record: Readonly<Record<string, unknown>>): number {
  const value = record['order'];
  return typeof value === 'number' && Number.isFinite(value)
    ? value
    : Number.MAX_SAFE_INTEGER;
}

function normalizeAllowedImageUrl(
  value: string,
  allowWorkspaceBlobFallback: boolean | undefined
): string | null {
  const normalized = value.trim();
  if (!normalized) return null;
  if (!allowWorkspaceBlobFallback && isWorkspaceBlobUrl(normalized)) {
    return null;
  }
  return normalized;
}

function isWorkspaceBlobUrl(value: string): boolean {
  return /^\/api\/workspaces\/[^/]+\/blobs\/[^/?#]+/.test(value);
}

function parseWorkspaceBlobUrl(
  value: string
): { workspaceId: string; blobId: string } | null {
  const match = /^\/api\/workspaces\/([^/]+)\/blobs\/([^/?#]+)/.exec(value);
  if (!match) return null;
  return {
    workspaceId: decodeURIComponent(match[1]),
    blobId: decodeURIComponent(match[2]),
  };
}

function isPublishedResponsiveImageAsset(
  value: unknown
): value is PublishedResponsiveImageAsset {
  if (!value || typeof value !== 'object') return false;
  const record = value as Record<string, unknown>;
  return (
    record['kind'] === 'responsive-image' &&
    typeof record['src'] === 'string' &&
    isVariantList((record['srcSet'] as Record<string, unknown>)?.['avif']) &&
    isVariantList((record['srcSet'] as Record<string, unknown>)?.['webp']) &&
    isVariantList((record['srcSet'] as Record<string, unknown>)?.['fallback'])
  );
}

function isVariantList(value: unknown): value is ResponsiveImageVariant[] {
  return (
    Array.isArray(value) &&
    value.every(
      item =>
        item &&
        typeof item === 'object' &&
        Number.isInteger((item as ResponsiveImageVariant).width) &&
        typeof (item as ResponsiveImageVariant).url === 'string'
    )
  );
}

function toSrcSet(variants: ResponsiveImageVariant[]): string {
  return variants.map(variant => `${variant.url} ${variant.width}w`).join(', ');
}
